import { useEffect, useRef } from 'react';

// Dependency-free Cloudflare Turnstile widget (explicit render). Renders nothing
// when no site key is configured — local/dev works without it, and the API also
// skips verification when its secret is unset.
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(script);
  });
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  /** Bump to force a fresh challenge (the previous token was consumed). */
  resetSignal?: number;
  /** Widget theme — set 'dark' on dark-background forms. Default follows the OS. */
  theme?: 'light' | 'dark' | 'auto';
  /** Widget size — 'flexible' fills the container width (min 300px). */
  size?: 'normal' | 'flexible' | 'compact';
}

export default function Turnstile({
  onVerify,
  onExpire,
  resetSignal,
  theme,
  size,
}: TurnstileProps) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const isFirstReset = useRef(true);

  useEffect(() => {
    if (!SITE_KEY || !ref.current) return;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => onVerify(token),
          'expired-callback': () => onExpire?.(),
          ...(theme ? { theme } : {}),
          ...(size ? { size } : {}),
        });
      })
      .catch(() => {
        /* network/CSP failure — the form falls back to submitting without a token */
      });

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parent bumps resetSignal after a submit → issue a fresh, unused token.
  useEffect(() => {
    if (isFirstReset.current) {
      isFirstReset.current = false;
      return;
    }
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  }, [resetSignal]);

  if (!SITE_KEY) return null;

  return <div ref={ref} className="my-1" />;
}
